import React, {Suspense, lazy, useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import {CheckCheckIcon, RefreshIcon, SparklesIcon, XIcon} from '../icons';
import {
    AiRecognitionRequest,
    RECOGNITION_STATUS_LABELS,
    isInProgress,
    isReviewable,
    toEditItem,
} from '../../utils/aiRecognition';

interface RecognitionReviewActionProps {
    /** Заявка, заведённая действием стилиста. */
    requestId: string;
    /** Вещь сохранена из чата — родитель может показать тост/ссылку. */
    onSaved?: (item: {id: string; name?: string}) => void;
    /**
     * Подпись кнопки проверки. По умолчанию — про сохранение новой вещи (сценарий чата);
     * повторный анализ из карточки обновляет существующую и говорит об этом (FR-4).
     */
    reviewLabel?: string;
    /** Подпись завершённого состояния. */
    savedLabel?: string;
}

// Форма карточки тяжёлая (справочники, загрузка фото) и нужна только по клику — тянем лениво,
// чтобы не утяжелять ленту чата.
const AddClothingItemModal = lazy(() => import('../AddClothingItemModal'));

/** Как часто перезапрашиваем заявку, пока она в работе. */
const POLL_MS = 5000;

/**
 * Проверка распознанной вещи прямо в чате (stylist-recognition-in-chat).
 *
 * Стилист заводит заявку по фото, но вещь в гардеробе появляется только после того, как
 * пользователь проверил поля — раньше для этого нужно было уходить на экран
 * [AI-распознавание](/ai-recognition). Здесь та же форма открывается модальным окном поверх
 * диалога: подтверждение остаётся, лишний переход исчезает.
 *
 * Пока заявка в очереди/распознаётся — показывается статус и кнопка неактивна; готовая
 * открывает форму, сохранённая показывает, что вещь уже заведена.
 */
const RecognitionReviewAction: React.FC<RecognitionReviewActionProps> = ({
    requestId,
    onSaved,
    reviewLabel = 'Проверить и сохранить',
    savedLabel = 'Вещь сохранена в гардероб',
}) => {
    const [request, setRequest] = useState<AiRecognitionRequest | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [saved, setSaved] = useState(false);
    /** Заявку не удалось получить: вечный «Загружаем…» вводил бы в заблуждение. */
    const [loadFailed, setLoadFailed] = useState(false);
    /** Счётчик ручных повторов — перезапускает эффект загрузки. */
    const [attempt, setAttempt] = useState(0);

    const load = useCallback(async () => {
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AiRecognitionRequest>(`/ai-recognition/${requestId}`);
            setRequest(res.data);
            setLoadFailed(false);
            return res.data;
        } catch {
            setLoadFailed(true);
            return null;
        }
    }, [requestId]);

    // Пока заявка в работе — опрашиваем: результат приходит из фоновой очереди, а не в ответе.
    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const tick = async () => {
            const loaded = await load();
            if (cancelled) return;
            if (isInProgress(loaded?.status)) {
                timer = setTimeout(tick, POLL_MS);
            }
        };
        tick();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [load, attempt]);

    const handleSuccess = useCallback((item?: {id: string; name?: string}) => {
        setModalOpen(false);
        setSaved(true);
        load();
        if (item?.id) onSaved?.(item);
    }, [load, onSaved]);

    const status = request?.status;
    const editItem = toEditItem(request);

    if (saved || status === 'DONE') {
        return (
            <span
                data-testid={`recognition-${requestId}-saved`}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-medium
                           text-brand-600 dark:text-brand-400"
            >
                <CheckCheckIcon size={16}/> {savedLabel}
            </span>
        );
    }

    if (loadFailed && !request) {
        return (
            <span
                data-testid={`recognition-${requestId}-error`}
                className="inline-flex items-center gap-x-2 gap-y-1 flex-wrap text-sm"
            >
                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                    <XIcon size={16}/> Не удалось получить заявку
                </span>
                <button
                    type="button"
                    onClick={() => setAttempt(n => n + 1)}
                    data-testid={`recognition-${requestId}-retry`}
                    className="inline-flex items-center gap-1 min-h-[44px] px-3 font-medium
                               text-brand-600 dark:text-brand-400 hover:underline"
                >
                    <RefreshIcon size={16}/> Повторить
                </button>
            </span>
        );
    }

    if (!isReviewable(status) || !editItem) {
        return (
            <span
                data-testid={`recognition-${requestId}-status`}
                data-status={status ?? 'LOADING'}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm
                           text-gray-500 dark:text-gray-400"
            >
                <SparklesIcon className="w-4 h-4"/>
                {status ? RECOGNITION_STATUS_LABELS[status] ?? status : 'Загружаем заявку…'}
            </span>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid={`recognition-${requestId}-review`}
                className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl
                           text-sm font-medium transition bg-brand-600 text-white hover:bg-brand-700"
            >
                <SparklesIcon className="w-4 h-4"/> {reviewLabel}
            </button>
            {modalOpen && (
                // Форма грузится отдельным чанком: пока он едет, пользователь должен видеть,
                // что нажатие сработало.
                <Suspense fallback={
                    <span role="status" aria-live="polite" data-testid={`recognition-${requestId}-loading`}
                          className="inline-flex items-center gap-2 min-h-[44px] px-3 text-sm text-gray-500 dark:text-gray-400">
                        <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"/>
                        Открываем форму…
                    </span>
                }>
                    <AddClothingItemModal
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        onSuccess={() => handleSuccess()}
                        editItem={editItem}
                        aiDetectedFlaws={request?.aiResult?.detectedFlaws ?? null}
                        aiConfirmRequestId={requestId}
                        aiConfirmUpdateMode={!!request?.sourceItemId}
                        onAiConfirmSuccess={(item) => handleSuccess(item as {id: string; name?: string})}
                    />
                </Suspense>
            )}
        </>
    );
};

export default RecognitionReviewAction;
